<template>
  <div id="shipping-settings">
    <div id="view-sub-nav">
      <v-btn
        variant="outlined"
        color="primary"
        @click="
          () => {
            dialogMode = 'Create'
            selectedItem = newItemForm
            itemDialog = true
          }
        "
        >Create New Shipping Zone</v-btn
      >
      <v-spacer />
      <v-text-field
        v-model="tableSearch"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        density="compact"
        :clearable="true"
      ></v-text-field>
    </div>
    <v-data-table
      v-if="shippingZones"
      :items="shippingZones"
      :loading="loading"
      :headers="headers"
      :sort-by="[{ key: 'name', order: 'asc' }]"
      :items-per-page="-1"
      :search="tableSearch"
      id="materials-table"
    >
      <template v-slot:item.regions="{ item }">
        {{ regionLabels(item.regions) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <td class="d-flex justify-end align-center">
          <v-tooltip location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon
                size="small"
                class="mr-1"
                @click="
                  () => {
                    dialogMode = 'Edit'
                    itemDialog = true
                    selectedItem = item
                  }
                "
                v-bind="props"
                >mdi-pencil</v-icon
              >
            </template>
            <span>Edit Shipping Zone</span>
          </v-tooltip>
          <v-tooltip v-if="$store.state.user.permissions.admin" location="top" :open-delay="500">
            <template v-slot:activator="{ props }">
              <v-icon
                size="small"
                @click="
                  () => {
                    deleteDialog = true
                    selectedItem = item
                  }
                "
                v-bind="props"
                >mdi-delete</v-icon
              >
            </template>
            <span>Delete Shipping Zone</span>
          </v-tooltip>
        </td>
      </template>
    </v-data-table>

    <!-- CREATE -->
    <v-dialog v-model="itemDialog" width="800">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">
          {{ dialogMode == 'Create' ? 'Create New Shipping Zone' : 'Edit Shipping Zone' }}
        </v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field label="Name" v-model="selectedItem.name"></v-text-field>
            <v-select
              v-model="selectedItem.regions"
              :items="regions"
              multiple
              label="Region"
              @update:model-value="onRegionsChange"
            ></v-select>
            <v-autocomplete
              v-if="selectedItem.regions && selectedItem.regions.includes('eu')"
              v-model="selectedItem.country"
              :items="euCountryItems"
              label="Country"
              clearable
            ></v-autocomplete>
            <v-autocomplete
              v-if="selectedItem.regions && selectedItem.regions.includes('us')"
              v-model="selectedItem.state"
              :items="$utils.usStateCodes"
              label="State"
              clearable
            ></v-autocomplete>
            <v-text-field
              label="Cost"
              v-model.number="selectedItem.cost"
              type="number"
              min="0"
              step="any"
            ></v-text-field>
            <div v-if="selectedItem.regions && selectedItem.regions.includes('eu') && selectedItem.cost > 0">
              <v-text-field
                label="Insurance"
                v-model.number="selectedItem.insurance"
                type="number"
                min="0"
                step="any"
              ></v-text-field>
              <v-text-field
                label="Customs Clearance"
                v-model.number="selectedItem.customsClearance"
                type="number"
                min="0"
                step="any"
              ></v-text-field>
              <v-text-field
                label="Import Vat"
                v-model.number="selectedItem.importVat"
                type="number"
                min="0"
                step="any"
              ></v-text-field>
              <v-text-field
                label="Transit Time"
                v-model.number="selectedItem.transitTime"
                type="number"
                min="0"
                step="1"
                suffix="weeks"
                :rules="[transitTimeRule]"
              ></v-text-field>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="
              () => {
                itemDialog = false
                resetNewItemValues()
              }
            "
            >Cancel</v-btn
          >
          <v-btn
            color="accent"
            variant="flat"
            @click="dialogMode === 'Create' ? createNewItem() : updateItem()"
            :disabled="submitting"
            >{{ dialogMode === 'Create' ? 'Create' : 'Update' }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card>
        <v-card-title class="bg-warning text-white text-h6">Delete Shipping Zone</v-card-title>
        <v-card-text class="pt-6"> Are you sure you want to delete this shipping zone? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="warning" variant="flat" @click="deleteItem(selectedItem)">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ALLOWED_COUNTRIES_EU, EU_COUNTRY_LABELS } from '@/js/checkoutConfig'
import { db } from '@/js/db'
import { collection, deleteDoc, doc, setDoc } from 'firebase/firestore'

export default {
  name: 'ShippingSettings',
  data() {
    return {
      loading: true,
      data: null,
      tableSearch: null,
      headers: [
        {
          title: 'Name',
          sortable: true,
          key: 'name'
        },
        {
          title: 'Region',
          sortable: false,
          key: 'regions'
        },
        {
          title: 'Country',
          sortable: false,
          key: 'country'
        },
        {
          title: 'State',
          sortable: false,
          key: 'state'
        },
        {
          title: 'Customs Clearance',
          sortable: false,
          key: 'customsClearance'
        },
        {
          title: 'Insurance',
          sortable: false,
          key: 'insurance'
        },
        {
          title: 'Import VAT',
          sortable: false,
          key: 'importVat'
        },
        {
          title: 'Cost',
          sortable: false,
          key: 'cost'
        },
        {
          title: 'Actions',
          sortable: false,
          align: 'end',
          key: 'actions'
        }
      ],
      deleteDialog: false,
      itemDialog: false,
      submitting: false,
      selectedItem: {},
      dialogMode: null,
      regions: [
        { text: 'UK', value: 'uk' },
        { text: 'EU', value: 'eu' },
        { text: 'US', value: 'us' }
      ],
      newItemForm: {},
      shippingZones: null
    }
  },
  computed: {
    euCountryItems() {
      return ALLOWED_COUNTRIES_EU.map((code) => ({
        value: code,
        text: EU_COUNTRY_LABELS[code] || code
      })).sort((a, b) => a.text.localeCompare(b.text))
    }
  },
  async mounted() {
    this.resetNewItemValues()
    await this.$live('shippingZones', collection(db, 'shippingZones'))
    this.loading = false
  },
  methods: {
    resetNewItemValues() {
      this.newItemForm = {
        name: null,
        regions: [],
        country: null,
        state: null,
        cost: null,
        insurance: null,
        customsClearance: null,
        importVat: null,
        transitTime: null
      }
    },
    regionLabels(regions) {
      if (!regions || !regions.length) return '—'
      return regions
        .map((code) => this.regions.find((r) => r.value === code)?.text || String(code).toUpperCase())
        .join(', ')
    },
    onRegionsChange(regions) {
      if (!regions.includes('eu')) {
        this.selectedItem.country = null
      }
      if (!regions.includes('us')) {
        this.selectedItem.state = null
      }
    },
    transitTimeRule(v) {
      if (v === null || v === '' || v === undefined) return true
      return Number.isInteger(Number(v)) || 'Transit time must be a whole number'
    },
    async createNewItem() {
      this.submitting = true

      if (!this.newItemForm.hasGrain) {
        this.newItemForm.colours = []
      }

      const newDocRef = doc(collection(db, 'shippingZones'))
      await setDoc(newDocRef, this.newItemForm)

      this.resetNewItemValues()
      this.submitting = false
      this.itemDialog = false
    },
    async updateItem() {
      this.submitting = true
      await setDoc(doc(db, 'shippingZones', this.selectedItem.id), this.selectedItem)

      this.submitting = false
      this.itemDialog = false
      this.$store.commit('openSnackbar', {
        snackbarText: 'Shipping Zone Updated'
      })
    },
    deleteItem(item) {
      this.deleteDialog = false
      deleteDoc(doc(db, 'shippingZones', item.id))
        .then(() => {
          this.$store.commit('openSnackbar', {
            snackbarText: 'Shipping Zone Deleted'
          })
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.colour {
  min-width: 30px;
  width: 30px;
  height: 30px;
  border: solid 1px #999;
  border-radius: 50%;
  margin-right: 5px;
}

#material-settings {
  height: 100%;
  display: flex;
  flex-direction: column;
  #materials-table {
    flex: 1;
    position: relative;
  }
}
</style>
